import {
	INFOPAGES_ERROR,
	INFOPAGES_SET,
	INFOPAGES_LOADED,
	INFOPAGE_REMOVED,
	INFOPAGES_PURGE,
	WELCOMEPAGES_LOADED,
	WELCOMEPAGE_REMOVED,
	WELCOMEPAGES_PURGE,
} from "./types";
import { infoActions } from "../webapi";

/** Load info pages
 * @param {string} site - The site to load info pages for
 */
export const loadInfoNodes = (site) => async (dispatch) => {
	dispatch({ type: INFOPAGES_LOADED, payload: true });
	try {
		const res = await infoActions.getInfoPages(site);
		const data = Array.isArray(res && res.data) ? res.data : [];
		const pages = data.map((p) => ({ ...p, order: Number(p.order || 0) }));
		dispatch({ type: INFOPAGES_SET, payload: pages });
		return pages;
	} catch (err) {
		dispatch({ type: INFOPAGES_ERROR, payload: err });
		throw err;
	} finally {
		dispatch({ type: INFOPAGES_LOADED, payload: false });
	}
};

/** Reorder info pages
 * @param {string} site - The site to reorder info pages for
 * @param {*} newList - The new ordered list of info pages
 * @example
 * const newList = [
 *   { id: '123', order: 0, title...},
 *   { id: '456', order: 1, title...},
 *   { id: '789', order: 2, title...},
 * ];
 * dispatch(reorderInfoNodes('plussSpace', newList));
 */
export const reorderInfoNodes =
	(site, newList) => async (dispatch, getState) => {
		const { infoPages: currentList } = getState().infoPages;
		dispatch({ type: INFOPAGES_LOADED, payload: true });
		if (!Array.isArray(newList)) {
			console.warn("reorderInfoNodes: newList is not an array");
			return;
		}
		if (newList.length === 0) {
			console.warn("reorderInfoNodes: newList is empty");
			return;
		}
		if (
			!newList.every((p) => {
				const keys = Object.keys(p);
				return (
					keys.includes("RowId") &&
					keys.includes("order") &&
					keys.includes("parentId")
				);
			})
		) {
			console.warn(
				"reorderInfoNodes: each entry must include RowId, order, and parentId",
			);
			return;
		}

		try {
			dispatch({ type: INFOPAGES_SET, payload: newList });
			await infoActions.changeInfoPageOrder(
				site,
				newList.map((p) => ({
					id: p.RowId,
					order: p.order,
					parentId: p.parentId || "root",
				})),
			);
		} catch (err) {
			dispatch({ type: INFOPAGES_SET, payload: currentList }); // Rollback
			dispatch({ type: INFOPAGES_ERROR, payload: err });
			throw err;
		} finally {
			dispatch({ type: INFOPAGES_LOADED, payload: false });
		}
	};

/**
 * Deletes a node and refreshes the tree from the server.
 * @param {{ site: string, id: string }} params
 */
export const deleteInfoNode =
	({ site, id }) =>
	async (dispatch) => {
		if (!site || !id) return;
		try {
			await infoActions.deleteInfoPage(site, id);
			await dispatch(loadInfoNodes(site));
		} catch (err) {
			dispatch({ type: INFOPAGES_ERROR, payload: err });
			throw err;
		}
	};

export const clearInfoNodes = () => {
	return {
		type: INFOPAGES_PURGE,
	};
};

/**
 * Dispatches a thunk to create a new folder.
 * @async
 * @function
 * @param {{ site: string, name: string }} params - Folder details.
 * @returns {(dispatch: import('redux').Dispatch) => Promise<void>} A thunk action.
 */
export const createNewFolder =
	({ site, name }) =>
	async (dispatch) => {
		try {
			await infoActions.addInfoFolder({ name, site });
			await loadInfoNodes(site)(dispatch);
		} catch (e) {
			console.error(e);
			throw new Error("Something went wrong when creating the folder");
		}
	};

export const renameFolder =
	({ site, folderId, name }) =>
	async (dispatch) => {
		try {
			await infoActions.editInfoPage(
				site,
				folderId,
				name,
				"",
				undefined,
				[],
				false,
				undefined,
			);
			await loadInfoNodes(site)(dispatch);
		} catch (e) {
			console.error(e);
			throw new Error("Something went wrong when renaming the folder");
		}
	};

export const welcomePagesUpdate = (site) => {
	return (dispatch) => {
		infoActions.getWelcomePages(site).then((res) => {
			if (res.data != null) {
				dispatch({
					type: WELCOMEPAGES_LOADED,
					payload: res.data,
				});
			}
		});
	};
};

export const welcomePagesLoaded = (events) => {
	return {
		type: WELCOMEPAGES_LOADED,
		payload: events,
	};
};

export const removeWelcomePage = (id) => {
	return {
		type: WELCOMEPAGE_REMOVED,
		payload: id,
	};
};

export const clearWelcomePages = () => {
	return {
		type: WELCOMEPAGES_PURGE,
	};
};
